/**
 * Ramp Embed - Stripe Renderer
 */
import { type Stripe } from "@stripe/stripe-js";

import { logger } from "../logger";
import { EmbedError, type ExchangeSessionTokenResponse } from "../types";

interface MountStripeOptions {
  container: HTMLElement;
  issuerData: ExchangeSessionTokenResponse;
  nonce: string;
  stripe: Stripe;
  jti: string;
  onCopied: (field: "number" | "expiry" | "cvv" | "pin") => void;
}

export async function mountStripe({
  container,
  issuerData,
  nonce,
  stripe,
  jti,
  onCopied,
}: MountStripeOptions): Promise<void> {
  const startTime = performance.now();

  const { stripe_card_id, issuer_token, issuing_product, pin_is_set } =
    issuerData;

  if (!stripe_card_id) {
    throw new EmbedError("CARD_DISPLAY_FAILED", "Failed to load card");
  }

  const elements = stripe.elements();

  container.innerHTML = `
    <div class="card-details">
      <div class="card-field">
        <div class="card-field-label">Card number</div>
        <div class="card-field-row">
          <div id="card-number"></div>
          <div id="card-number-copy"></div>
        </div>
      </div>
      <div class="card-field-group">
        <div class="card-field">
          <div class="card-field-label">EXP</div>
          <div class="card-field-row">
            <div id="card-expiry"></div>
            <div id="card-expiry-copy"></div>
          </div>
        </div>
        <div class="card-field">
          <div class="card-field-label">CVV</div>
          <div class="card-field-row">
            <div id="card-cvc"></div>
            <div id="card-cvc-copy"></div>
          </div>
        </div>
        ${
          pin_is_set
            ? `
        <div class="card-field">
          <div class="card-field-label">PIN</div>
          <div id="card-pin"></div>
        </div>
        `
            : ""
        }
      </div>
    </div>
  `;

  const style = {
    base: {
      color: "#1a1a1a",
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: "16px",
    },
  };

  const commonOptions = {
    issuingCard: stripe_card_id,
    nonce,
    ephemeralKeySecret: issuer_token,
    style,
  };

  const number = elements.create("issuingCardNumberDisplay", commonOptions);
  const expiry = elements.create("issuingCardExpiryDisplay", commonOptions);
  const cvc = elements.create("issuingCardCvcDisplay", commonOptions);

  number.mount("#card-number");
  expiry.mount("#card-expiry");
  cvc.mount("#card-cvc");

  const numberCopy = elements.create("issuingCardCopyButton", {
    toCopy: "number",
    style,
  });
  const expiryCopy = elements.create("issuingCardCopyButton", {
    toCopy: "expiry",
    style,
  });
  const cvcCopy = elements.create("issuingCardCopyButton", {
    toCopy: "cvc",
    style,
  });

  numberCopy.mount("#card-number-copy");
  expiryCopy.mount("#card-expiry-copy");
  cvcCopy.mount("#card-cvc-copy");

  numberCopy.on("click", () => onCopied("number"));
  expiryCopy.on("click", () => onCopied("expiry"));
  cvcCopy.on("click", () => onCopied("cvv"));

  if (pin_is_set) {
    const pin = elements.create("issuingCardPinDisplay", commonOptions);
    pin.mount("#card-pin");
  }

  const duration = Math.round(performance.now() - startTime);
  logger.info("card_mount_success", {
    jti,
    product: issuing_product,
    duration_ms: duration,
  });
}
